import { useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';

/**
 * Nový hook pro synchronizaci stavu s URL parametry
 * Vrací stav a setter, který automaticky synchronizuje s URL
 * Obsahuje vnitřní stav - jednodušší použití
 */
export function useUrlSync<T extends Record<string, any>>(
  prefix: string,
  options: {
    /** Výchozí hodnoty pro parametry */
    defaults: T;
    /** Mapování klíčů stavu na URL parametry */
    keyMapping?: Partial<Record<keyof T, string>>;
    /** Funkce pro transformaci hodnoty před uložením do URL */
    serialize?: Partial<Record<keyof T, (value: any) => string>>;
    /** Funkce pro transformaci hodnoty při načítání z URL */
    deserialize?: Partial<Record<keyof T, (value: string) => any>>;
    /** Parametry, které se nemají synchronizovat s URL */
    exclude?: (keyof T)[];
  },
): [T, (newState: Partial<T>) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  const { defaults, keyMapping = {}, serialize = {}, deserialize = {}, exclude = [] } = options;

  // Vnitřní state
  const [state, setState] = useState<T>(() => defaults);

  // Vytvoření mapování klíčů
  const getUrlKey = useCallback(
    (stateKey: keyof T): string => {
      const mappedKey = (keyMapping as any)[stateKey];
      if (mappedKey) return mappedKey;

      const prefixedKey = prefix ? `${prefix}_${String(stateKey)}` : String(stateKey);
      return prefixedKey;
    },
    [prefix, keyMapping],
  );

  // Sledování změn URL a aktualizace vnitřního stavu
  useEffect(() => {
    const urlState: Partial<T> = {};

    Object.keys(defaults).forEach(key => {
      const stateKey = key as keyof T;
      if (exclude.includes(stateKey)) return;

      const urlKey = getUrlKey(stateKey);
      const urlValue = searchParams.get(urlKey);

      if (urlValue !== null) {
        try {
          const deserializer = (deserialize as any)[stateKey];
          const deserializedValue = deserializer ? deserializer(urlValue) : urlValue;
          (urlState as any)[stateKey] = deserializedValue;
        } catch (error) {
          console.warn(`Failed to deserialize URL parameter ${urlKey}:`, error);
        }
      }
    });

    // Aktualizuj vnitřní stav z URL pouze pokud se něco změnilo
    if (Object.keys(urlState).length > 0) {
      setState(prev => {
        // Zkontroluj, jestli se skutečně něco změnilo
        const hasChanges = Object.keys(urlState).some(key => (urlState as any)[key] !== (prev as any)[key]);

        if (hasChanges) {
          return { ...prev, ...urlState };
        }

        return prev; // Žádné změny, vrať stejný stav
      });
    }
  }, [searchParams, getUrlKey, deserialize, exclude, defaults]);

  // Setter - mění pouze URL, useEffect pak automaticky aktualizuje stav
  const setUrl = useCallback(
    (newState: Partial<T>) => {
      // Aktualizace URL
      const newSearchParams = new URLSearchParams(searchParams);

      Object.keys(newState).forEach(key => {
        const stateKey = key as keyof T;
        if (exclude.includes(stateKey)) return;

        const urlKey = getUrlKey(stateKey);
        const value = (newState as any)[stateKey];

        if (value === null || value === undefined || value === '') {
          newSearchParams.delete(urlKey);
        } else {
          const serializer = (serialize as any)[stateKey];
          const serializedValue = serializer ? serializer(value) : String(value);
          newSearchParams.set(urlKey, serializedValue);
        }
      });

      setSearchParams(newSearchParams, { replace: true });
      // useEffect se automaticky spustí a aktualizuje stav z URL
    },
    [searchParams, setSearchParams, getUrlKey, serialize, exclude],
  );

  return [state, setUrl];
}

/**
 * Pomocné funkce pro serializaci/deserializaci
 */
export const urlSyncUtils = {
  /** Serializace čísla */
  number: {
    serialize: (value: number | null): string => value?.toString() || '',
    deserialize: (value: string): number | null => {
      const parsed = parseInt(value, 10);
      return isNaN(parsed) ? null : parsed;
    },
  },

  /** Serializace boolean */
  boolean: {
    serialize: (value: boolean | null): string => value?.toString() || '',
    deserialize: (value: string): boolean | null => {
      if (value === 'true') return true;
      if (value === 'false') return false;
      return null;
    },
  },

  /** Serializace objektu (JSON) */
  object: {
    serialize: (value: any): string => {
      try {
        return JSON.stringify(value);
      } catch {
        return '';
      }
    },
    deserialize: (value: string): any => {
      try {
        return JSON.parse(value);
      } catch {
        return null;
      }
    },
  },

  /** Serializace pole */
  array: {
    serialize: (value: any[] | null): string => {
      if (!value || value.length === 0) return '';
      return value.join(',');
    },
    deserialize: (value: string): any[] | null => {
      if (!value) return null;
      return value.split(',').filter(Boolean);
    },
  },
};
